import React from "react";
import { Helmet } from "react-helmet";

import SITE, { absoluteUrl } from "../../data/siteMetadata";

const toJsonLd = (items) =>
	JSON.stringify(Array.isArray(items) ? items : [items]).replace(/</g, "\\u003c");

const SEOHead = ({
	title,
	description,
	path = "/",
	keywords = [],
	image = SITE.image,
	type = "website",
	noindex = false,
	jsonLd = [],
	language = SITE.language,
	locale = SITE.locale,
	alternates = [],
}) => {
	const pageTitle = title ? `${title} | ${SITE.name}` : SITE.title;
	const canonicalUrl = absoluteUrl(path);
	const imageUrl = absoluteUrl(image);
	const keywordsContent = Array.isArray(keywords) ? keywords.filter(Boolean).join(", ") : keywords;
	const robotsContent = noindex
		? "noindex, nofollow"
		: "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1";

	const baseJsonLd = [
		{
			"@context": "https://schema.org",
			"@type": "WebSite",
			"@id": `${SITE.url}/#website`,
			url: SITE.url,
			name: SITE.name,
			description: SITE.description,
			inLanguage: SITE.language,
			publisher: {
				"@id": `${SITE.url}/#person`,
			},
		},
		{
			"@context": "https://schema.org",
			"@type": "Person",
			"@id": `${SITE.url}/#person`,
			name: SITE.author.name,
			email: SITE.author.email,
			url: SITE.url,
			sameAs: [SITE.author.linkedin, SITE.author.github],
			jobTitle: "Digital product builder and data engineering specialist",
		},
	];

	const allJsonLd = [...baseJsonLd, ...jsonLd].filter(Boolean);

	return (
		<Helmet htmlAttributes={{ lang: language }}>
			<title>{pageTitle}</title>
			<meta name="description" content={description || SITE.description} />
			{keywordsContent ? <meta name="keywords" content={keywordsContent} /> : null}
			<meta name="robots" content={robotsContent} />
			<link rel="canonical" href={canonicalUrl} />
			{alternates.map((alternate) => (
				<link
					key={`${alternate.hrefLang}-${alternate.href}`}
					rel="alternate"
					hrefLang={alternate.hrefLang}
					href={absoluteUrl(alternate.href)}
				/>
			))}

			<meta property="og:locale" content={locale} />
			<meta property="og:site_name" content={SITE.name} />
			<meta property="og:type" content={type} />
			<meta property="og:title" content={pageTitle} />
			<meta property="og:description" content={description || SITE.description} />
			<meta property="og:url" content={canonicalUrl} />
			<meta property="og:image" content={imageUrl} />

			<meta name="twitter:card" content="summary_large_image" />
			<meta name="twitter:title" content={pageTitle} />
			<meta name="twitter:description" content={description || SITE.description} />
			<meta name="twitter:image" content={imageUrl} />

			<script type="application/ld+json">{toJsonLd(allJsonLd)}</script>
		</Helmet>
	);
};

export default SEOHead;
